import { useContext, useEffect, useState } from "react";
import useTranslator from "../../hooks/useTranslator";
import { haveValue, toast } from "../../helper/common";
import AuthContext from "../../context/auth/AuthContext";
import TenantContext from "../../context/tenant/TenantContext";
import CheckIcon from "../../assets/img/checkmark-circle-02.svg";
import ButtonActionTooltip from "../ErrorModal/ButtonActionTooltip";
import AuthStepHeader from "../Auth/AuthStepHeader";
import { Badge, Button, Icon, Notice, Select, TextField } from "../design";

const FlatmateOrGuarantorAddForm = ({
  isOpen,
  onClose,
  selectedUser,
  setModal,
  isGuarantor,
  handleSendEmailToGuarantor,
  editGuarantorRequestId,
  inline = false,
}) => {
  const { _t } = useTranslator();
  const { user, loadUser, getUserByEmail } = useContext(AuthContext);
  const {
    deleteSelectedOnboardingUser,
    addSharer,
    createBasicGuarantorAccount,
    updateBasicGuarantorAccount,
  } = useContext(TenantContext);
  const [addFlatmateLoading, setAddFlatmateLoading] = useState(false);
  const [guarantorAccountPresent, setGuarantorAccountPresent] = useState(false);
  const [editEmailAlreadyPresent, setEditEmailAlreadyPresent] = useState(false);

  const [formData, setFormData] = useState({
    fname: "",
    lname: "",
    email: "",
    relation: "",
  });

  useEffect(() => {
    if (editGuarantorRequestId && selectedUser) {
      setFormData({
        fname: selectedUser?.fname ?? "",
        lname: selectedUser?.lname ?? "",
        email: selectedUser?.email ?? "",
        relation:
          selectedUser?.relation ?? selectedUser?.gurantor_relation ?? "",
      });
      return;
    }

    if (haveValue(selectedUser?.name)) {
      const nameParts = selectedUser.name.trim().split(/\s+/);

      let fname = "";
      let lname = "";

      if (nameParts.length === 2) {
        fname = nameParts[0];
        lname = nameParts[1];
      } else if (nameParts.length > 2) {
        const mid = Math.floor(nameParts.length / 2);
        fname = nameParts.slice(0, mid).join(" ");
        lname = nameParts.slice(mid).join(" ");
      } else {
        fname = nameParts[0];
        lname = "";
      }

      setFormData({
        fname,
        lname,
      });
    } else {
      setFormData({
        fname: "",
        lname: "",
        email: "",
        relation: "",
      });
    }
  }, [editGuarantorRequestId, selectedUser]);

  const handleInputChange = (e) => {
    if (isGuarantor && !editGuarantorRequestId) {
      setGuarantorAccountPresent(false);
    }
    let { name, value } = e.target;
    if (name === "email") {
      value = value?.toLowerCase();
      setEditEmailAlreadyPresent(false);
    }
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmitSharer = async () => {
    try {
      setAddFlatmateLoading(true);
      const allFilled =
        haveValue(formData.fname) &&
        haveValue(formData.lname) &&
        haveValue(formData.email) &&
        haveValue(formData.relation);

      if (!allFilled) return;
      if (isGuarantor) {
        if (editGuarantorRequestId && editEmailAlreadyPresent) return;
        if (formData.email === user?.email) {
          toast.error(_t("youCannotAddYourselfAsGuarantor"));
          return;
        }
        const emailLookup = await getUserByEmail({
          email: formData.email,
          type: "tenant",
        });
        const existingUser = emailLookup?.user;
        const isCurrentEditedGuarantor =
          editGuarantorRequestId &&
          existingUser?._id?.toString() === selectedUser?._id?.toString();

        if (existingUser && !isCurrentEditedGuarantor) {
          if (editGuarantorRequestId) {
            setEditEmailAlreadyPresent(true);
          } else {
            setGuarantorAccountPresent(true);
            await handleSendEmailToGuarantor(formData.email);
          }
          return;
        }
        const payload = {
          fname: formData.fname,
          lname: formData.lname,
          email: formData.email,
          gurantor_relation: formData.relation,
          step: 1,
        };
        const response = editGuarantorRequestId
          ? await updateBasicGuarantorAccount({
              ...payload,
              guarantor_id: editGuarantorRequestId,
            })
          : await createBasicGuarantorAccount(payload);
        if (response?.status === "success") {
          await handleSendEmailToGuarantor(
            formData.email,
            editGuarantorRequestId
          );
        } else {
          toast.error(_t("failedToAddGuarantor"));
        }
      } else {
        const payload = {
          name: formData.fname + " " + formData.lname,
          email: formData.email,
          relation: formData.relation,
        };

        const res = await addSharer(payload);
        if (haveValue(selectedUser)) {
          const payload = {
            tenant_id: user?._id,
            ...selectedUser,
          };
          await deleteSelectedOnboardingUser(payload);
        }
        if (!res?.success) {
          let errorMessage = res?.response?.data?.message;
          if (errorMessage && isGuarantor) {
            errorMessage = _t("failedToAddGuarantor");
          } else if (errorMessage) {
            // keep the same error message
            errorMessage = _t(errorMessage);
          } else {
            errorMessage = _t("failedToAddSharer");
          }
          toast.error(errorMessage);
          return;
        }
        loadUser("tenant");
        closeModel();
      }
    } catch (err) {
      if (
        isGuarantor &&
        err?.response?.data?.errorKey === "account-already-present"
      ) {
        if (editGuarantorRequestId) {
          setEditEmailAlreadyPresent(true);
          return;
        }
        handleSendEmailToGuarantor(formData.email, editGuarantorRequestId);
        loadUser("tenant");
        return;
      }
      toast.error(
        (err?.response?.data?.message ?? isGuarantor)
          ? _t("failedToAddGuarantor")
          : _t("failedToAddSharer")
      );
    } finally {
      setAddFlatmateLoading(false);
    }
  };

  const closeModel = () => {
    setFormData({
      fname: "",
      lname: "",
      email: "",
      relation: "",
    });
    onClose();
  };

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const checkEmailPresent = async () => {
    const response = await getUserByEmail({
      email: formData.email,
      type: "tenant",
    });

    if (response) {
      const existingUser = response.user;
      const isCurrentEditedGuarantor =
        editGuarantorRequestId &&
        existingUser?._id?.toString() === selectedUser?._id?.toString();
      if (existingUser && !isCurrentEditedGuarantor) {
        if (editGuarantorRequestId) {
          setEditEmailAlreadyPresent(true);
        } else {
          setGuarantorAccountPresent(true);
        }
      }
    }
  };

  const disableButton =
    !haveValue(formData.fname) ||
    !haveValue(formData.lname) ||
    !haveValue(formData.email) ||
    !haveValue(formData.relation) ||
    !isValidEmail(formData.email) ||
    editEmailAlreadyPresent;

  if (!isOpen) return null;

  // Guarantor request as a normal page (next to the progress rail), not a popup
  if (inline && isGuarantor) {
    const blockBadChars = (e) => {
      if (!/^[A-Za-zÀ-ÿ\s'-]+$/.test(e.data)) e.preventDefault();
    };
    return (
      <main className="guarantorPage">
        <div className="guarantorPage__col">
          <AuthStepHeader
            onBack={() => setModal(false)}
            title={_t("guarantorDetails")}
            subtitle={_t("guarantorSendRequestSub")}
          />
          {guarantorAccountPresent && (
            <Notice tone="success">{_t("guarantorAlreadyPresent")}</Notice>
          )}
          <form
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              if (guarantorAccountPresent) {
                handleSendEmailToGuarantor(formData.email);
              } else if (!disableButton && !addFlatmateLoading) {
                handleSubmitSharer();
              }
            }}
          >
            <div className="guarantorPage__row">
              <TextField
                id="guarantor-fname"
                name="fname"
                label={_t("firstName")}
                placeholder={_t("enterFirstName")}
                value={formData.fname}
                onChange={handleInputChange}
                onBeforeInput={blockBadChars}
                required
              />
              <TextField
                id="guarantor-lname"
                name="lname"
                label={_t("lastName")}
                placeholder={_t("enterLastName")}
                value={formData.lname}
                onChange={handleInputChange}
                onBeforeInput={blockBadChars}
                required
              />
            </div>
            <Select
              id="guarantor-relation"
              name="relation"
              label={_t("relationshipWithGuarantor")}
              placeholder={_t("pleaseSelect")}
              value={formData.relation}
              onChange={handleInputChange}
              options={[
                { value: "parent", label: _t("parent") },
                { value: "Family", label: _t("Family") },
                { value: "relative", label: _t("relative") },
                { value: "partner", label: _t("partner") },
                { value: "company", label: _t("company") },
                { value: "other", label: _t("other") },
              ]}
            />
            <TextField
              id="guarantor-email"
              name="email"
              type="email"
              label={_t("emailOfGuarantor")}
              placeholder={_t("enterYourEmail")}
              value={formData.email}
              onChange={handleInputChange}
              onBlur={checkEmailPresent}
              error={
                editEmailAlreadyPresent ? _t("emailAlreadyExists") : undefined
              }
              required
            />
            <Button
              type="submit"
              variant="primary"
              size="lg"
              block
              className="guarantorPage__next"
              loading={addFlatmateLoading}
              disabled={guarantorAccountPresent ? false : disableButton}
            >
              {guarantorAccountPresent
                ? _t("sendRequestToAddGuarantor")
                : _t("sendRequest")}
            </Button>
          </form>
        </div>
      </main>
    );
  }

  return (
    <div
      className={`modal wrapper-modal show rentSharerDetailsModal fade ${
        isGuarantor ? "modal-fixed-screen" : ""
      }`}
      style={{ display: "block" }}
      onClick={() => setModal(false)}
    >
      <div
        className="modal-dialog modal-lg modal-dialog-centered"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-content">
          {/* Modal Header */}
          <div className="modal-header justify-content-between">
            <h5 className="modal-title">
              {isGuarantor ? _t("guarantorDetails") : _t("rentSharerDetails")}
            </h5>
            <div className="d-flex align-items-center custom-gap-2">
              {isGuarantor && guarantorAccountPresent && (
                <Badge>
                  <img src={CheckIcon} />
                  {_t("guarantorAlreadyPresent")}
                </Badge>
              )}
              <Button
                variant="ghost"
                size="sm"
                iconOnly
                iconStart={<Icon name="close" />}
                aria-label={_t("close")}
                onClick={() => setModal(false)}
              />
            </div>
          </div>

          {/* Modal Body */}
          <div className="modal-body">
            <div className="row form-flatmate">
              {/* Form Section */}
              <form onSubmit={(e) => e.preventDefault()}>
                <div className="row g-3">
                  <div className="col-md-6">
                    <TextField
                      id="flatmate-fname"
                      label={_t("firstName")}
                      type="text"
                      placeholder={_t("enterFirstName")}
                      name="fname"
                      value={formData.fname}
                      onChange={handleInputChange}
                      onBeforeInput={(e) => {
                        const isValidChar = /^[A-Za-zÀ-ÿ\s'-]+$/.test(e.data);
                        if (!isValidChar) e.preventDefault();
                      }}
                      required
                    />
                  </div>

                  <div className="col-md-6">
                    <TextField
                      id="flatmate-lname"
                      label={_t("lastName")}
                      type="text"
                      placeholder={_t("enterLastName")}
                      name="lname"
                      value={formData.lname}
                      onChange={handleInputChange}
                      onBeforeInput={(e) => {
                        const isValidChar = /^[A-Za-zÀ-ÿ\s'-]+$/.test(e.data);
                        if (!isValidChar) e.preventDefault();
                      }}
                      required
                    />
                  </div>

                  <div className="col-md-6">
                    <Select
                      id="flatmate-relation"
                      label={_t("relationshipWithSharer")}
                      name="relation"
                      placeholder={_t("pleaseSelect")}
                      value={formData?.relation}
                      onChange={handleInputChange}
                      options={
                        isGuarantor
                          ? [
                              { value: "parent", label: _t("parent") },
                              { value: "Family", label: _t("Family") },
                              { value: "relative", label: _t("relative") },
                              { value: "partner", label: _t("partner") },
                              { value: "company", label: _t("company") },
                              { value: "other", label: _t("other") },
                            ]
                          : [
                              { value: "friend", label: _t("friend") },
                              { value: "wife", label: _t("wife") },
                              { value: "couple", label: _t("couple") },
                              { value: "colleague", label: _t("colleague") },
                              { value: "other", label: _t("other") },
                            ]
                      }
                    />
                  </div>

                  <div className="col-md-6">
                    <TextField
                      id="flatmate-email"
                      label={
                        isGuarantor
                          ? _t("emailOfGuarantor")
                          : _t("emailOfSharer")
                      }
                      type="email"
                      placeholder={_t("enterYourEmail")}
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      onBeforeInput={(e) => {
                        if (!e.data) return; // allow delete, etc.

                        const value = e.data;

                        // Allow full pasted string
                        const isValid = /^[a-z0-9._%+-@]+$/.test(value);

                        if (!isValid) {
                          e.preventDefault();
                          return;
                        }

                        // Prevent multiple @ (handle paste too)
                        const existingValue = e.target.value;
                        const totalAtCount =
                          (existingValue + value).split("@").length - 1;

                        if (totalAtCount > 1) {
                          e.preventDefault();
                        }
                      }}
                      onBlur={() => (isGuarantor ? checkEmailPresent() : {})}
                      error={
                        editEmailAlreadyPresent
                          ? _t("emailAlreadyExists")
                          : undefined
                      }
                    />
                  </div>
                </div>
              </form>
            </div>
          </div>

          {/* Modal Footer */}
          <div className="modal-footer">
            {isGuarantor && guarantorAccountPresent ? (
              <Button
                size="lg"
                disabled={addFlatmateLoading}
                onClick={async () => {
                  try {
                    setAddFlatmateLoading(true);
                    await handleSendEmailToGuarantor(formData.email);
                  } finally {
                    setAddFlatmateLoading(false);
                  }
                }}
              >
                {addFlatmateLoading
                  ? `${_t("sending")}...`
                  : _t("sendRequestToAddGuarantor")}
              </Button>
            ) : (
              <div
                data-tooltip-id="button-action-tooltip"
                style={{ display: "inline-block", width: "100%" }}
              >
                <Button
                  size="lg"
                  disabled={disableButton || addFlatmateLoading}
                  onClick={handleSubmitSharer}
                >
                  {addFlatmateLoading
                    ? `${_t("sending")}...`
                    : isGuarantor
                      ? _t("sendRequest")
                      : _t("addFlatmate")}
                </Button>
                {(disableButton || addFlatmateLoading) && (
                  <ButtonActionTooltip />
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FlatmateOrGuarantorAddForm;
